/**
 * One-shot guard for the modular request-tender wizard's INITIAL auto-skip
 * (skipping a preset step — e.g. profession when `?professionId=` is set).
 *
 * Pure module on purpose: no Vue / GraphQL imports, so it is unit-testable in
 * isolation (the wizard components transitively import `.graphql`, which Jest
 * can't transform).
 *
 * Scope = a single ENTRY into the wizard, NOT the browser tab:
 *   - Module scope (not a component ref) survives the per-step page remounts
 *     within one wizard session, so navigating back to a prefilled
 *     profession/city step is NOT bounced forward again.
 *   - Module scope resets on a full page load, so a genuinely fresh entry
 *     auto-skips again.
 *   - For client-side (SPA) re-entry from another page — e.g. starting a
 *     second tender from a different professional in the same tab — the page
 *     middleware calls `resetAutoSkipForEntry()` (gated by `isFreshWizardEntry`)
 *     so the skip re-arms.
 *
 * Regression context: this used to be a `sessionStorage` flag that was set
 * once and never cleared, so it suppressed the auto-skip for every tender
 * after the first one in a tab, stranding the user on the already-prefilled
 * profession step behind a loader.
 *
 * Note: the flag is only ever read/written on the client (the auto-skip runs
 * in `onMounted`; the middleware reset is `process.client`-guarded), so the
 * shared module scope is never touched during SSR and can't leak between
 * server requests.
 */

let autoSkipDoneForEntry = false;

export function resetAutoSkipForEntry() {
  autoSkipDoneForEntry = false;
}

export function markAutoSkipDone() {
  autoSkipDoneForEntry = true;
}

export function autoSkipAlreadyDone() {
  return autoSkipDoneForEntry;
}

/**
 * Is navigating from `fromName` to `toName` a fresh ENTRY into the wizard
 * (vs. a step-to-step move within it)? i18n suffixes (`name___hu`) are
 * stripped so a same-route `activeTab` param change compares equal and is
 * NOT treated as a fresh entry.
 *
 * @param {string|null|undefined} fromName - route name navigated FROM
 * @param {string|null|undefined} toName - route name navigated TO
 * @returns {boolean} true when the wizard is entered from a different route
 */
export function isFreshWizardEntry(fromName, toName) {
  const baseName = (name) => (name || "").split("___")[0];
  return baseName(fromName) !== baseName(toName);
}
